import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { FeatureFlag, useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { redirectToLogin } from '../services/sessionNavigation';
import { isMaintenanceBypassActive } from '../utils/maintenanceBypass';
import { GOOGLE_SIGNUP_RESUME_PATH } from '../utils/googleSignupFlow';
import { appendDirectoryClaim } from '../utils/directoryClaimSlug';
import MaintenancePage from '../pages/MaintenancePage';
import MagicLoader from './MagicLoader/MagicLoader';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { t } = useTranslation('login');
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const location = useLocation();
  const isBackofficeLocked = !isFeatureEnabled(FeatureFlag.BACKOFFICE_ACCESS);

  const hasToken = localStorage.getItem('token');
  const isEmployeeSession = localStorage.getItem('is_employee_session') === 'true';
  const storedProId = localStorage.getItem('pro_id');
  const hasProId = user?.pro_id || storedProId;

  // 1. Token absent = session morte (même si React croit encore être connecté :
  //    autre onglet a logout, localStorage vidé, listes encore en mémoire).
  if (!isLoading && !hasToken) {
    if (location.pathname.startsWith('/app')) {
      const fullPath = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
      localStorage.setItem('redirectAfterAuth', fullPath);
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 2. Pendant la vérification du token : loader plein écran uniquement
  if (isLoading) {
    return <MagicLoader />;
  }

  // 3. Contenu / redirections métier
  if (isAuthenticated && !hasProId && !isEmployeeSession) {
    if (Capacitor.isNativePlatform()) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column',
            padding: '40px 24px',
            textAlign: 'center',
            backgroundColor: '#0a0a0a',
            color: 'white',
          }}
        >
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>🐾</div>
          <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '12px' }}>
            {t('protectedRouteNative.welcomeTitle')}
          </h2>
          <p
            style={{
              fontSize: '16px',
              color: 'rgba(255,255,255,0.7)',
              lineHeight: '1.6',
              maxWidth: '340px',
              marginBottom: '32px',
            }}
          >
            <Trans
              i18nKey="protectedRouteNative.welcomeDescription"
              ns="login"
              components={{ strong: <strong /> }}
            />
          </p>
          <button
            type="button"
            onClick={() => {
              logout();
              redirectToLogin();
            }}
            style={{
              padding: '14px 32px',
              backgroundColor: '#c2773e',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 600,
            }}
          >
            {t('protectedRouteNative.backToLogin')}
          </button>
        </div>
      );
    }
    return <Navigate to={appendDirectoryClaim(GOOGLE_SIGNUP_RESUME_PATH)} replace />;
  }

  if (isBackofficeLocked && !isMaintenanceBypassActive()) {
    return <MaintenancePage />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
